import {
  Action,
  createPickSelector,
  createSelector, Selector,
  State, StateContext,
  StateToken, Store
} from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { AuthStateModel, LogoutAction, SetUserAndAuthStatus } from './core/authentication/auth.state.model';
import { AuthState } from './core/authentication/auth.state';
import { ConsumerJsonModel } from './consumer/consumer.json.model';
import { SubjectJsonModel } from './subject/subject.json.model';
import { AppActions } from './app.actions';
import { ConsumerService } from './consumer/consumer.service';
import { SubjectService } from './subject/services/subject.service';
import { TranslationService } from './core/services/translationService';
import { environment } from '../environments/environment';

export class SetIsLoaded {
  static readonly type = '[App] Set Is Loaded';
  constructor(public isLoaded: boolean) { }
}

const appStateToken = new StateToken<AppStateModel>('appState');

export interface AppStateModel {
  readonly isLoaded: boolean
  readonly consumers: ConsumerJsonModel[]
  readonly subjects: SubjectJsonModel[]
}

@State<AppStateModel>({
  name: appStateToken,
  defaults: {
    isLoaded: false,
    consumers: [],
    subjects: []
  }
})
@Injectable()
export class AppState {
  private store = inject(Store);
  private translationService = inject(TranslationService);
  private consumerService = inject(ConsumerService);
  private subjectService = inject(SubjectService);

  @Selector([AuthState])
  static isAuthenticated(authState: AuthStateModel): boolean {
    return authState.isAuthenticated;
  }

  @Selector([AuthState])
  static userRole(authState: AuthStateModel): string {
    return authState.editor?.role || '';
  }

  @Selector()
  static isLoaded(state: AppStateModel) {
    return state.isLoaded;
  }

  @Action(AppActions.RestoreAuthState)
  restoreAuthState(ctx: StateContext<AppStateModel>) {
    const isAuthenticated = localStorage.getItem('isAuthenticated') !== null;
    const editor = JSON.parse(localStorage.getItem('editor') || 'null');

    // Restore Auth-State first
    this.store.dispatch(new SetUserAndAuthStatus({ isAuthenticated, userRole: editor?.role }));

    if (isAuthenticated) {
      this.store.dispatch(new AppActions.LoadInitialData());
    }
    else {
      ctx.patchState({ isLoaded: true });
    }
  }

  @Action(AppActions.LoadInitialData)
  loadInitialData(ctx: StateContext<AppStateModel>) {
    if (!environment.isProd) {
      console.log('::==> Loading initial data... <==::');
    }

    if (localStorage.getItem('access_token') !== null) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      this.loadTranslations((t) => {
        this.loadConsumers((consumers) => {
          if (!environment.isProd) {
            console.log('::==> Consumers loaded <==::');
          }
          ctx.patchState({ consumers: [...consumers.sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base', numeric: true }))] });
          this.loadSubjects((subjects) => {
            if (!environment.isProd) {
              console.log('::==> Subjects loaded <==::');
            }
            ctx.patchState({
              subjects: [...subjects.sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base', numeric: true }))],
              isLoaded: true
            });
          });
        });
      });
    }
  }

  @Action(LogoutAction)
  onLogout(ctx: StateContext<AppStateModel>) {
    ctx.patchState({ isLoaded: false, consumers: [], subjects: [] });
  }

  @Action(AppActions.StartupAfterLogin)
  onStartupAfterLogin(ctx: StateContext<AppStateModel>, action: AppActions.StartupAfterLogin) {
    console.log(`::==> Startup after login, newLogin ? ${action.isNewLogin} <==::`);

    this.loadTranslations((t) => {
      console.log(`::==> Translations loaded ${t} <==::`);
      this.loadConsumers((consumers) => {
        ctx.patchState({ consumers: [...consumers.sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base', numeric: true }))] });
        this.loadSubjects((subjects) => {
          ctx.patchState({
            subjects: [...subjects.sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base', numeric: true }))],
            isLoaded: true
          });
        });
      });
    });
  }

  private loadTranslations(callback: (result: Record<string, string>) => void) {
    this.translationService.loadTranslations().subscribe(translations => {
      this.translationService.setTranslations(translations);
      callback(translations);
    });
  }

  private loadConsumers(callback: (result: ConsumerJsonModel[]) => void) {
    this.consumerService.getAllModels({}, {
    }).subscribe({
      next: (result) => {
        callback(result);
      },
      error: () => {
        // is handled in requestInterceptor.ts
      }
    });
  }

  private loadSubjects(callback: (result: SubjectJsonModel[]) => void) {
    this.subjectService.getAllModels({
      queryParams: {
        filter: {
          root: 'true'
        }
      }
    }, {
    }).subscribe({
      next: (result) => {
        callback(result);
      },
      error: () => {
        // is handled in requestInterceptor.ts
      }
    });
  }
}

export class AppSelectors {
  static getFullAppState = createSelector([AppState], (state: AppStateModel) => state);
  static getFullAuthState = createSelector([AuthState], (state: AuthStateModel) => state);

  static authState = createPickSelector(this.getFullAuthState, [
    'isAuthenticated',
    'editor'
  ]);

  static appState = createPickSelector<AppStateModel, (keyof AppStateModel)[]>(this.getFullAppState, [
    'isLoaded',
    'consumers',
    'subjects'
  ]);

  static userRole = createSelector([this.getFullAuthState], (authState: AuthStateModel) => {
    return authState.editor?.role || '';
  });
}
